import { icpFactTextOf, isJevReadItem, type IcpFact, type IcpFactItem, type IcpFactType, type IcpItem, type IcpItemRequest, type IcpRating } from '@nest-recruit/shared';

// ADR 0061 / ADR 0062 — the ideal candidate profile as a form: its lines in and out of what the server takes, the ratings laid over
// the server's until its refetch lands, and "Ask me more questions" merging a new draft in. Used by the form and the interview
// (this feature), the Ranking card and the job setup (which import it). Pure, so each is tested without a screen.

/** Ratings clicked but not yet confirmed by the server, by line id: laid over the server's until its refetch lands (CLAUDE.md §5a rule 3). */
export type RatingOverlay = Readonly<Record<string, IcpRating>>;

/** The ratings on screen: the server's, with the clicks not yet confirmed laid over them. */
export function withRatings(items: readonly IcpItem[], overlay: RatingOverlay): readonly IcpItem[] {
  return items.map((item) => {
    const rating = overlay[item.id];
    return rating === undefined || rating === item.rating ? item : { ...item, rating };
  });
}

/** One string for the ratings in force, so a change of them is a change of this. */
export function ratingsKeyOf(items: readonly IcpItem[]): string {
  return items.map((item) => `${item.id}:${item.rating}`).join('|');
}

/** Whether every line is rated as the form last saved it — Reset focus has nothing to do. */
export function isAtBaseline(items: readonly IcpItem[], baseline: Readonly<Record<string, IcpRating>>): boolean {
  return items.every((item) => baseline[item.id] === undefined || baseline[item.id] === item.rating);
}

/** The ratings of the form's last save, as the overlay Reset focus shows at once. */
export function baselineOverlay(items: readonly IcpItem[], baseline: Readonly<Record<string, IcpRating>>): RatingOverlay {
  const overlay: Record<string, IcpRating> = {};
  for (const item of items) {
    const rating = baseline[item.id];
    if (rating !== undefined) overlay[item.id] = rating;
  }
  return overlay;
}

/** The lines Jev reads: the number the form's footer counts out of 10. */
export function jevLineCount(items: readonly { readonly kind: IcpItem['kind'] }[]): number {
  return items.filter((item) => item.kind === 'jev').length;
}

/** A fact line as the form starts it, with the values most jobs want. */
export function newFactItem(type: IcpFactType, id: string): IcpFactItem {
  const fact: IcpFact =
    type === 'years'
      ? { type, minYears: 2, maxYears: null }
      : type === 'education'
        ? { type, minLevel: 'bachelors' }
        : type === 'location'
          ? { type, city: '', aliases: [] }
          : type === 'notice'
            ? { type, withinDays: 30 }
            : { type };
  return { id, kind: 'fact', group: 'facts', fact, text: icpFactTextOf(fact), rating: 3, source: 'added' };
}

/** A line as the browser sends it: a fact line carries no text, because the app writes it. */
export function itemInputOf(item: IcpItem): IcpItemRequest {
  if (item.kind === 'fact') {
    const fact = item.fact.type === 'location' ? { type: 'location' as const, city: item.fact.city, aliases: [...item.fact.aliases] } : item.fact;
    return { id: item.id, kind: 'fact', group: 'facts', fact, rating: item.rating, source: item.source };
  }
  if (item.kind === 'english') return { id: item.id, kind: 'english', group: 'communication', text: item.text, evidence: item.evidence, rating: item.rating, source: item.source };
  return { id: item.id, kind: 'jev', group: item.group, text: item.text, evidence: item.evidence, rating: item.rating, source: item.source };
}

/** The form's lines as the card shows them once saved: a fact's title written by the app. */
export function itemOfInput(input: IcpItemRequest): IcpItem {
  if (input.kind === 'fact') {
    const fact: IcpFact = input.fact.type === 'years' ? { ...input.fact, maxYears: input.fact.maxYears ?? null } : input.fact.type === 'location' ? { ...input.fact, aliases: input.fact.aliases ?? [] } : input.fact;
    return { id: input.id, kind: 'fact', group: 'facts', fact, text: icpFactTextOf(fact), rating: input.rating, source: input.source };
  }
  if (input.kind === 'english') return { id: input.id, kind: 'english', group: 'communication', text: input.text, evidence: input.evidence ?? '', rating: input.rating, source: input.source };
  return { id: input.id, kind: 'jev', group: input.group, text: input.text, evidence: input.evidence ?? '', rating: input.rating, source: input.source };
}

function isSameContent(a: IcpItem, b: IcpItem): boolean {
  if (a.kind === 'fact' && b.kind === 'fact') return JSON.stringify(a.fact) === JSON.stringify(b.fact);
  return isJevReadItem(a) && isJevReadItem(b) && a.text === b.text && a.evidence === b.evidence;
}

/**
 * "Ask me more questions": the new draft replaces the form, but a line keeps the rating the recruiter gave it when
 * the draft still has it (same id) and its words have not changed.
 */
export function mergeDraftIntoForm(previous: readonly IcpItem[], draft: readonly IcpItem[]): readonly IcpItem[] {
  return draft.map((item) => {
    const before = previous.find((entry) => entry.id === item.id);
    return before && isSameContent(before, item) ? { ...item, rating: before.rating } : item;
  });
}

/** "about 6¢" out of "Ranking 96 CVs with Jev · about 6¢"; null when the estimate has no price (nothing new to read, no CVs yet). */
export function estimateCostOf(label: string): string | null {
  const at = label.lastIndexOf(' · ');
  return at < 0 ? null : label.slice(at + 3);
}
